import { css, type Theme } from '@emotion/react';
import styled from '@emotion/styled';
import { InteractiveText, Ui } from '@silvertours/front-shared';
import { Link } from '../../Link';

type Mode = { $mode: 'mobile' | 'desktop' | 'floating' };

/**
 * The lg rules are for the desktop navigation bar only. The mobile navigation
 * keeps its layout at all widths. The floating mode is the menu of the floating
 * header: the links are right aligned and use the button font of the design.
 */
const Nav = styled.nav<Mode>`
  display: ${({ $mode }) => ($mode === 'desktop' ? 'none' : 'flex')};
  justify-content: ${({ $mode }) =>
    $mode === 'floating' ? 'flex-end' : 'center'};
  overflow: ${({ $mode }) => ($mode === 'desktop' ? 'hidden' : 'initial')};

  ${({ $mode, theme }) =>
    $mode === 'desktop' &&
    css`
      ${theme.mq('lg')} {
        display: block;
      }
    `}
`;

const LinkList = styled.ul<{ hideChildren: number } & Mode>`
  max-width: 400px;
  margin-bottom: ${({ theme }) => theme.spacing[20]};
  margin-left: ${({ theme }) => theme.spacing[40]};
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: flex-end;

  ${({ hideChildren }) => css`
    & > :nth-of-type(-n + ${hideChildren}) {
      visibility: hidden;
    }
  `};

  ${({ $mode, theme }) =>
    $mode === 'desktop' &&
    css`
      ${theme.mq('lg')} {
        margin-bottom: 0;
        margin-left: 0;
        max-width: none;
        flex-direction: row;
        padding-top: 0;
      }
    `}

  ${({ $mode, theme }) =>
    $mode === 'floating' &&
    css`
      max-width: none;
      margin-bottom: 0;
      margin-left: 0;
      align-items: flex-end;
      gap: ${theme.spacing[40]};

      ${theme.mq('md')} {
        gap: ${theme.spacing[20]};
      }
    `}
`;

const LinkListItem = styled.li<{ container: 'navbar' | 'popover' } & Mode>`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  position: relative;
  margin-bottom: ${({ $mode, theme }) =>
    $mode === 'floating' ? 0 : theme.spacing[60]};
  white-space: nowrap;

  ${({ $mode, container, theme }) =>
    $mode === 'desktop' &&
    css`
      ${theme.mq('lg')} {
        height: ${container === 'navbar' && '50px'};
        margin-bottom: ${container === 'navbar' ? '25px' : 0};
        align-items: ${container === 'navbar' && 'end'};
      }
    `}
`;

const getLinkStyles = ({ $mode, theme }: Mode & { theme: Theme }) => css`
  color: ${theme.colors.brand.green140};
  font-family: ${theme.fontFamily.default};

  ${
    $mode === 'desktop' &&
    css`
      ${theme.mq('lg')} {
        color: ${theme.colors.neutral.grey160};
      }
    `
  }

  ${
    $mode === 'floating' &&
    css`
      color: ${theme.colors.brand.green120};
      font-family: ${theme.fontFamily.alternate};
      font-size: ${theme.fontSize.sm.size};
      font-weight: ${theme.fontWeight.heading};
      line-height: ${theme.fontSize.sm.lineHeight};
    `
  }

  &:focus-visible {
    border-radius: ${theme.radius.xs};
    outline: 0.125rem dashed ${theme.colors.brand.orange100};
    outline-offset: 0.25rem;
  }

  &:hover,
  &:active {
    text-decoration: underline;
  }
`;

const withoutMode = { shouldForwardProp: (prop: string) => prop !== '$mode' };

const MaskedLink = styled(Ui.MaskedLink, withoutMode)<Mode>`
  ${getLinkStyles};
`;

const Anchor = styled(Link, withoutMode)<Mode>`
  ${getLinkStyles}
  text-decoration: none;
`;

const StyledText = styled(InteractiveText, withoutMode)<
  { withPadding: boolean } & Mode
>`
  ${({ $mode, theme, withPadding }) =>
    $mode === 'desktop' &&
    css`
      ${theme.mq('lg')} {
        padding-left: ${withPadding ? theme.spacing[50] : 0};
        margin-top: 0;
        list-style-type: none;
      }

      ${theme.mq('xl')} {
        padding-left: ${withPadding ? theme.spacing[60] : 0};
      }
    `}
`;

const StyledBadge = styled(Ui.Badge, withoutMode)<
  { container: 'navbar' | 'popover' } & Mode
>`
  font-size: 0.75rem; /* @TODO: Move this to Ui.Badge once the legacy header has been removed. */

  ${({ $mode, container, theme }) =>
    $mode === 'desktop' &&
    css`
      ${theme.mq('lg')} {
        position: ${container === 'navbar' && 'absolute'};
        top: ${container === 'navbar' && '0.5rem'};
        right: ${container === 'navbar' && '-0.75rem'};
      }
    `}
`;

export {
  LinkList,
  LinkListItem,
  Anchor,
  MaskedLink,
  Nav,
  StyledText,
  StyledBadge,
};
